// src/pages/lab/processing/components/AnalysisListTab.tsx
import React, { useState } from "react";
import { Button, Input, Space, Typography, Modal, Select } from "antd";
import { SearchOutlined, CloseCircleOutlined, PlusOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";

// Shared components
import { DataTable, PageToolbar, SectionCard, StatusTag } from "../../../../components/shared";

// Components
import StartAnalysisProtocolModal from "./components/StartAnalysisProtocolModal";

// Types
import { SampleAnalysis } from "../../../../types/lab/SampleTypes";
import { Protocol, ProcessingGroup } from "../../../../types/lab/ProtocolTypes";

// Utils and hooks
import { useNotifications } from "../../../../context/NotificationContext";
import { loadUserPreferences } from "../../../../utils/userPrefs/UserPrefs";

const { Text } = Typography;
const { Option } = Select;

interface AnalysisListTabProps {
	analyses: SampleAnalysis[];
	protocols: Protocol[];
	pendingGroups: ProcessingGroup[];
	loading: boolean;
	onRefresh: () => void;
	onAssignToGroup: (groupId: number, analysesToAssign: SampleAnalysis[]) => void;
	assigningToGroup: boolean;
}

const AnalysisListTab: React.FC<AnalysisListTabProps> = ({ analyses, protocols, pendingGroups, loading, onRefresh, onAssignToGroup, assigningToGroup }) => {
	const [selectedAnalyses, setSelectedAnalyses] = useState<SampleAnalysis[]>([]);

	const PAGE_SIZE = loadUserPreferences().tableRowsPerPage;

	// Modal states
	const [startProtocolModalOpen, setStartProtocolModalOpen] = useState(false);
	const [assignToGroupModalOpen, setAssignToGroupModalOpen] = useState(false);
	const [selectedGroupId, setSelectedGroupId] = useState<number | undefined>(undefined);

	const { showError, showWarning, showSuccess } = useNotifications();

	const handleStartNewProtocol = () => {
		if (selectedAnalyses.length === 0) {
			showWarning("Please select at least one analysis");
			return;
		}
		setStartProtocolModalOpen(true);
	};

	const handleAssignToExistingGroup = () => {
		if (selectedAnalyses.length === 0) {
			showWarning("Please select at least one analysis");
			return;
		}

		// Filter for pending groups with non-first protocols
		const analysisGroups = pendingGroups.filter((g) => g.protocol && !g.protocol.is_first_protocol);

		if (analysisGroups.length === 0) {
			showWarning("No pending analysis processing groups available. Please create a new group.");
			return;
		}

		setAssignToGroupModalOpen(true);
	};

	const handleConfirmAssignToGroup = async () => {
		if (!selectedGroupId) {
			showWarning("Please select a group");
			return;
		}

		const analysesToAssign = selectedAnalyses.map((a) => ({
			...a,
			sampleID: a.analysis_id, // Map analysis_id to sampleID for the API
		})) as any;

		await onAssignToGroup(selectedGroupId, analysesToAssign);

		setAssignToGroupModalOpen(false);
		setSelectedGroupId(undefined);
		setSelectedAnalyses([]);
	};

	const handleProtocolStarted = () => {
		setStartProtocolModalOpen(false);
		setSelectedAnalyses([]);
		onRefresh();
	};


	const columns: ColumnsType<SampleAnalysis> = [
		{
			title: "Analysis ID",
			dataIndex: "analysis_id",
			key: "analysis_id",
			fixed: "left",
			render: (text: string) => text || "-",
		},
		{
			title: "Sample ID",
			dataIndex: "sample_id",
			key: "sample_id",
			render: (text: string) => text || "-",
		},
		{
			title: "Test Type",
			dataIndex: "protocol_name",
			key: "protocol_name",
			render: (text: string) => text || "-",
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status: string) => <StatusTag status={status || "pending"} />,
		},
		{
			title: "Assigned",
			dataIndex: "assigned_at",
			key: "assigned_at",
			render: (date: string) => {
				if (!date) return <Text type="secondary">-</Text>;
				return <Text style={{ fontSize: "13px" }}>{new Date(date).toLocaleDateString()}</Text>;
			},
		},
	];

	// Filter pending groups for analysis processing only
	const analysisPendingGroups = pendingGroups.filter((g) => g.protocol && !g.protocol.is_first_protocol);

	return (
		<>
			<PageToolbar
				extraActions={[
					{
						label: `Start New Protocol (${selectedAnalyses.length})`,
						icon: <PlusOutlined />,
						onClick: handleStartNewProtocol,
						disabled: selectedAnalyses.length === 0,
					},
					{
						label: "Add to Pending Group",
						icon: <PlusOutlined />,
						onClick: handleAssignToExistingGroup,
						disabled: selectedAnalyses.length === 0,
					},
					{
						label: `Clear Selection (${selectedAnalyses.length})`,
						icon: <CloseCircleOutlined />,
						onClick: () => setSelectedAnalyses([]),
						disabled: selectedAnalyses.length === 0,
					}
				]}
			/>

			<DataTable
				bare
				columns={columns}
				dataSource={analyses}
				rowKey="id"
				loading={loading}
				selectedRows={selectedAnalyses}
				setSelectedRows={setSelectedAnalyses}
				scrollX={1000}
				scrollY="calc(100vh - 305px)"
				pagination={{ pageSize: PAGE_SIZE }}
				style={{ marginTop: 12 }}
			/>

			{/* Start Protocol Modal */}
			<StartAnalysisProtocolModal
				isOpen={startProtocolModalOpen}
				analyses={selectedAnalyses}
				protocols={protocols}
				onClose={() => setStartProtocolModalOpen(false)}
				onProtocolStarted={handleProtocolStarted}
			/>

			{/* Assign to Existing Group Modal */}
			<Modal
				title="Add Analyses to Pending Group"
				open={assignToGroupModalOpen}
				onCancel={() => { setAssignToGroupModalOpen(false); setSelectedGroupId(undefined); }}
				onOk={handleConfirmAssignToGroup}
				okText="Add to Group"
				confirmLoading={assigningToGroup}
			>
				<Space direction="vertical" size="middle" style={{ width: "100%" }}>
					<Text>Select a pending analysis processing group to add {selectedAnalyses.length} selected analysis(es):</Text>
					<Select style={{ width: "100%" }} placeholder="Select a group" value={selectedGroupId} onChange={setSelectedGroupId}>
						{analysisPendingGroups.map((group) => (
							<Option key={group.id} value={group.id}>
								<div>
									<Text strong>{group.group_name}</Text>
									<br />
									<Text type="secondary" style={{ fontSize: "11px" }}>
										{group.protocol?.name} v{group.protocol?.version} • {group.sample_count} analyses
									</Text>
								</div>
							</Option>
						))}
					</Select>
					{analysisPendingGroups.length === 0 && (
						<Text type="secondary">No pending analysis groups available. Create a new group instead.</Text>
					)}
				</Space>
			</Modal>
		</>
	);
};

export default AnalysisListTab;
